import { Children, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { IconArrowsMaximize, IconDownload, IconMinus, IconPlus } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { notifications } from '@/lib/notifications';
import { useMediaQuery } from '@/lib/hooks';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../utils/bnFormat';
import { capturePages, downloadBlob, pagesToPdf, printPages } from '../../utils/posterExport';
import EditorModalShell, { EditorBarFrame, EditorBarPrimary, EditorBarToolButton, EditorBarTools } from './EditorModalShell';
import PosterExportDialog from './PosterExportDialog';

const EXPORT_TOAST_ID = 'poster-export';
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/**
 * The shared poster editor — the app's editor-window design (EditorModalShell: black canvas with the live poster, 400px side panel
 * with tabs, bottom bar with zoom and the primary action) plus the whole way out: PDF, image and print. It knows NOTHING about what
 * a poster shows; the feature hands it the pages and the panels:
 *
 *   tabs         [{ id, label, title, desc, Icon, content }]   the side-panel tabs (only the active one's `content` is mounted)
 *   renderPages  ({ preview }) => page elements — every page root carries `data-poster-page` and is `pageSize` big. `preview`
 *                is true on screen (add a shadow if you like) and false for the clean, un-scaled copy that gets exported.
 *   pageSize     { w, h } of ONE page in CSS px — drives the fit-to-screen zoom and the export proportions
 *   pageCount    how many pages renderPages returns (shown in the export dialog)
 *   fitKey       when it changes the zoom goes back to "fit" (a new layout, paper size …)
 *   exportConfig { stem, sheetMm: [w, h] of the real sheet, imageFormat: 'png' | 'jpeg', imagePixelRatio, pdfPixelRatio, settleMs,
 *                  prepare: async () => … (load fonts / inline images before the copy is captured),
 *                  ready: false → nothing to export yet (shows emptyMessage), emptyMessage, meta: line under the dialog title }
 *   extras       small controls (e.g. a language switch) — in the header on a desktop window, else at the top of the side panel
 *   metaLine     a short summary line beside them
 *   icon, title, titleShort, onClose    window header (the red close button)
 *   children     extra hidden nodes the feature needs (e.g. a measuring ruler)
 */
export default function PosterEditor({
    icon, title, titleShort, onClose,
    extras = null, metaLine = '',
    tabs, defaultTab,
    pageSize, pageCount = 1, renderPages, fitKey,
    exportConfig = {},
    children,
}) {
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 767px)');
    const digits = (n) => (i18n.language?.startsWith('en') ? String(n) : toBnDigits(n));

    const [tab, setTab] = useState(defaultTab || tabs[0]?.id);
    const [zoomOverride, setZoomOverride] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [busy, setBusy] = useState(null);             // 'pdf' | 'image' | 'print' while one is being made
    const [exporting, setExporting] = useState(false);  // mounts the hidden, un-scaled copy of the pages

    useEffect(() => { setZoomOverride(null); }, [fitKey, pageSize.w, pageSize.h]);

    // ── zoom: fit the canvas by default ──
    const stageRef = useRef(null);
    const [stageSize, setStageSize] = useState({ w: 700, h: 600 });
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const measure = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const fitW = (stageSize.w - 8) / pageSize.w;
    const fitH = (stageSize.h - 8) / pageSize.h;
    // a whole page when it fits, else the width (the canvas then scrolls); a phone always fits the width
    const fit = isMobile ? clamp(fitW, 0.3, 1) : clamp(Math.max(Math.min(fitW, fitH), Math.min(fitW, 0.65)), 0.3, 1.25);
    const zoom = zoomOverride ?? fit;
    const stepZoom = (dir) => setZoomOverride(clamp(Math.round((zoom + dir * 0.1) * 10) / 10, 0.3, 1.8));

    // plural is chosen by the real number (`count`); the digits shown follow the app's language (`n`)
    const countLabel = (key, count) => t(`posterEditor.${key}`, { count, n: digits(count) });

    // ── export: a hidden, un-scaled copy of the pages is what gets captured ──
    const exportRef = useRef(null);
    const imageFormat = exportConfig.imageFormat === 'jpeg' ? 'jpeg' : 'png';
    const runExport = async (kind) => {
        if (busy) return;
        if (exportConfig.ready === false) { notifications.show({ message: exportConfig.emptyMessage || t('posterEditor.export.failed'), color: 'orange' }); return; }
        setBusy(kind);
        notifications.show({ id: EXPORT_TOAST_ID, message: t(kind === 'pdf' ? 'posterEditor.export.makingPdf' : kind === 'image' ? 'posterEditor.export.makingImages' : 'posterEditor.export.preparing'), loading: true });
        try {
            await exportConfig.prepare?.();
            flushSync(() => setExporting(true));
            const stage = exportRef.current;
            await Promise.all(Array.from(stage.querySelectorAll('img'), (img) => img.decode?.().catch(() => {})));
            await new Promise((resolve) => setTimeout(resolve, exportConfig.settleMs ?? 120)); // let the copy lay out (a timer, not rAF: rAF stops in a background tab)
            const nodes = Array.from(stage.querySelectorAll('[data-poster-page]'));
            const stem = exportConfig.stem || 'poster';
            const sheetMm = exportConfig.sheetMm || [210, 297];

            if (kind === 'image') {
                const blobs = await capturePages(nodes, { format: imageFormat, pixelRatio: exportConfig.imagePixelRatio ?? 3 });
                const ext = imageFormat === 'jpeg' ? 'jpg' : 'png';
                blobs.forEach((blob, i) => downloadBlob(blob, `${stem}${blobs.length > 1 ? `-${i + 1}` : ''}.${ext}`));
                notifications.update({ id: EXPORT_TOAST_ID, message: countLabel('export.imageDone', blobs.length), color: 'green' });
            } else if (kind === 'pdf') {
                const blobs = await capturePages(nodes, { format: 'jpeg', pixelRatio: exportConfig.pdfPixelRatio ?? 4 });
                downloadBlob(await pagesToPdf(blobs, sheetMm), `${stem}.pdf`);
                notifications.update({ id: EXPORT_TOAST_ID, message: t('posterEditor.export.pdfDone'), color: 'green' });
            } else {
                const blobs = await capturePages(nodes, { format: 'jpeg', pixelRatio: exportConfig.pdfPixelRatio ?? 4 });
                printPages(blobs, sheetMm, stem);
                notifications.hide(EXPORT_TOAST_ID);
            }
            setExportOpen(false);
        } catch (err) {
            console.error('[poster-editor] export failed', err?.message || err);
            notifications.update({ id: EXPORT_TOAST_ID, message: t('posterEditor.export.failed'), color: 'red' });
        } finally {
            setExporting(false);
            setBusy(null);
        }
    };

    // ── the shell's slots ──
    const active = tabs.find((x) => x.id === tab) || tabs[0];
    const extrasInHeader = !isMobile;
    const pages = Children.toArray(renderPages({ preview: true }));

    const canvas = (
        <div ref={stageRef} className="w-full h-full min-h-0 overflow-auto flex">
            <div className="m-auto flex flex-wrap justify-center items-start" style={{ gap: 32 * zoom }}>
                {pages.map((page, i) => (
                    // scaled with a transform (not CSS zoom) so container-query units inside the page keep their meaning
                    <div key={i} className="flex-none" style={{ width: pageSize.w * zoom, height: pageSize.h * zoom }}>
                        <div style={{ width: pageSize.w, height: pageSize.h, transform: `scale(${zoom})`, transformOrigin: '0 0' }}>{page}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const sidebar = (
        <div className="flex flex-col flex-1 min-h-0">
            {!extrasInHeader && (extras || metaLine) && (
                <div className="shrink-0 flex items-center justify-between gap-2 px-3 pt-2">
                    {extras}
                    {metaLine && <span className="text-[11px] text-zinc-400 dark:text-zinc-500 truncate">{metaLine}</span>}
                </div>
            )}
            <div className="shrink-0 grid gap-1 p-2 border-b border-zinc-200 dark:border-zinc-700" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }} role="tablist">
                {tabs.map(({ id, label, title: tabTitle, Icon }) => {
                    const on = active.id === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={on}
                            aria-label={tabTitle || label}
                            onClick={() => setTab(id)}
                            className={cn(
                                'flex flex-col items-center gap-1 py-2 rounded-lg transition-colors',
                                on ? 'bg-primary/10 text-primary' : 'text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                            )}
                        >
                            {Icon && <Icon size={isMobile ? 18 : 20} stroke={1.8} />}
                            <span className="text-[11px] font-semibold leading-tight text-center">{label}</span>
                        </button>
                    );
                })}
            </div>
            <div className={cn('flex-1 min-h-0 overflow-y-auto', isMobile ? 'p-3' : 'p-4')} role="tabpanel">
                {active.desc && <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug mb-4">{active.desc}</p>}
                {active.content}
            </div>
            {isMobile && (
                <div className="shrink-0 border-t border-zinc-200 dark:border-zinc-700 px-3 py-2">
                    <button
                        type="button"
                        onClick={() => setExportOpen(true)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors"
                    >
                        <IconDownload size={18} />
                        {t('posterEditor.top.downloadShort')}
                    </button>
                </div>
            )}
        </div>
    );

    const bottomBar = isMobile ? null : (
        <EditorBarFrame>
            <EditorBarTools>
                <EditorBarToolButton onClick={() => stepZoom(-1)} aria-label={t('posterEditor.stage.zoomOut')}><IconMinus size={20} strokeWidth={2.5} /></EditorBarToolButton>
                <span className="inline-flex items-center justify-center text-[13px] font-bold text-zinc-700 dark:text-zinc-200" style={{ minWidth: 54, padding: '0 6px' }}>
                    {digits(Math.round(zoom * 100))}%
                </span>
                <EditorBarToolButton onClick={() => stepZoom(1)} aria-label={t('posterEditor.stage.zoomIn')}><IconPlus size={20} strokeWidth={2.5} /></EditorBarToolButton>
                <EditorBarToolButton wide gap={12} tone="indigo" onClick={() => setZoomOverride(null)}>
                    <IconArrowsMaximize size={18} /> {t('posterEditor.stage.fit')}
                </EditorBarToolButton>
            </EditorBarTools>
            <EditorBarPrimary onClick={() => setExportOpen(true)}>
                <IconDownload size={18} />
                {t('posterEditor.top.download')}
            </EditorBarPrimary>
        </EditorBarFrame>
    );

    return (
        <>
            <EditorModalShell
                isMobile={isMobile}
                icon={icon}
                title={isMobile && titleShort ? titleShort : title}
                headerExtra={extrasInHeader && (extras || metaLine) ? (
                    <div className="flex items-center gap-3 ml-3 min-w-0 overflow-hidden">
                        {extras}
                        {metaLine && <span className="text-xs text-zinc-400 dark:text-zinc-500 whitespace-nowrap truncate">{metaLine}</span>}
                    </div>
                ) : null}
                onClose={onClose}
                leftStyle={isMobile ? { flex: '1 1 45%', maxHeight: '50svh' } : undefined}
                canvas={canvas}
                sidebar={sidebar}
                bottomBar={bottomBar}
            />

            <PosterExportDialog
                open={exportOpen}
                onOpenChange={setExportOpen}
                busy={busy}
                onRun={runExport}
                pageCount={pageCount}
                meta={exportConfig.meta}
                imageLabel={imageFormat === 'jpeg' ? 'JPG' : 'PNG'}
            />

            {children}
            {exporting && createPortal(
                <div ref={exportRef} aria-hidden="true" style={{ position: 'fixed', left: -100000, top: 0, display: 'flex', flexDirection: 'column', pointerEvents: 'none' }}>
                    {Children.toArray(renderPages({ preview: false }))}
                </div>,
                document.body,
            )}
        </>
    );
}
